/**
 * ProtocolStep: the Protocol step, between Discover and the point picker (web
 * brief U3). The discovered device's protocol is pre-selected and can be
 * overridden, but only with a driver this gateway may use
 * (`GET /v1/things/drivers?gateway_id=` → `available`).
 *
 * Progressive disclosure: most operators keep the scan's pick, so the step
 * shows only the picked protocol with "Change". The full list (with a reason
 * for each protocol this gateway cannot use, e.g. S7 on old firmware) opens
 * on "Change", and on its own when the picked protocol is not available here.
 * The connection form (children) sits under it.
 *
 * Without a drivers list (a server older than the multi-protocol backend), the
 * device keeps its own protocol (Modbus TCP for everything such a server can
 * discover), exactly as before this step existed.
 */

import { useState, type ReactNode } from "react";
import { Plug } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { Skeleton } from "@/lib/ui/skeleton";
import { cn } from "@/lib/ui/cn";
import type { DriverInfo } from "@/lib/api";
import type { DiscoveredDevice } from "@/lib/gateway-client";
import { driverLabel, driverStatusBadge, protocolChoices } from "@/lib/iot/drivers";

export interface ProtocolStepProps {
  device: DiscoveredDevice;
  drivers: DriverInfo[] | undefined;
  driversLoading: boolean;
  /** The drivers list failed or this server has none: the device keeps its own protocol. */
  driversUnavailable: boolean;
  driverId: string;
  onDriverId: (id: string) => void;
  /** The connection form for the chosen protocol, rendered under the picker. */
  children?: ReactNode;
}

export function ProtocolStep({
  device,
  drivers,
  driversLoading,
  driversUnavailable,
  driverId,
  onDriverId,
  children,
}: ProtocolStepProps) {
  const choices = protocolChoices(drivers);
  const found = device.online && device.driver === driverId;
  const picked = choices.find((c) => c.id === driverId);
  const [changing, setChanging] = useState(false);
  // The list shows while changing, and whenever the pick cannot be used here (so its reason shows).
  const expanded = changing || !picked || !picked.available;
  const pickedBadge = picked ? driverStatusBadge(picked.status) : "";

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Plug className="size-5 text-brand" />
            How to talk to {device.name ?? device.ip}
          </CardTitle>
          <CardDescription>
            {device.online
              ? `The scan found this device on ${driverLabel(device.driver, drivers)}.`
              : "Pick the protocol the device speaks."}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {driversLoading ? (
            <div aria-busy="true" aria-label="Loading protocols">
              <Skeleton className="h-12 w-full" />
            </div>
          ) : driversUnavailable || choices.length === 0 ? (
            <p className="text-xs text-on-surface-muted">
              This server does not list protocols per gateway, so the device is read over{" "}
              <span className="font-medium text-on-surface">{driverLabel(device.driver)}</span>.
            </p>
          ) : !expanded && picked ? (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-brand/40 bg-brand-muted/20 px-3 py-2.5">
              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-sm font-medium text-on-surface">
                {picked.label}
                <span className="text-[11px] font-normal text-on-surface-muted">
                  {picked.medium === "serial" ? "serial" : "Ethernet"}
                </span>
                {pickedBadge && (
                  <Badge variant="secondary" className="text-[10px]">
                    {pickedBadge}
                  </Badge>
                )}
                {found && (
                  <Badge variant="success" className="text-[10px]">
                    Found by the scan
                  </Badge>
                )}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setChanging(true)} aria-label="Change protocol">
                Change
              </Button>
            </div>
          ) : (
            <>
              <div role="radiogroup" aria-label="Protocol" className="flex flex-col gap-1.5">
                {choices.map((c) => {
                  const on = c.id === driverId;
                  const badge = driverStatusBadge(c.status);
                  return (
                    <label
                      key={c.id}
                      className={cn(
                        "flex items-start gap-3 rounded-md border px-3 py-2 text-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                        on ? "border-brand bg-brand-muted/30" : "border-border",
                        c.available ? "cursor-pointer" : "cursor-not-allowed opacity-70",
                      )}
                    >
                      <input
                        type="radio"
                        name="connect-protocol"
                        className="mt-1"
                        value={c.id}
                        checked={on}
                        disabled={!c.available}
                        onChange={() => {
                          onDriverId(c.id);
                          setChanging(false);
                        }}
                      />
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="flex flex-wrap items-center gap-2 font-medium text-on-surface">
                          {c.label}
                          <span className="text-[11px] font-normal text-on-surface-muted">
                            {c.medium === "serial" ? "serial" : "Ethernet"}
                          </span>
                          {badge && (
                            <Badge variant="secondary" className="text-[10px]">
                              {badge}
                            </Badge>
                          )}
                          {found && on && (
                            <Badge variant="success" className="text-[10px]">
                              Found by the scan
                            </Badge>
                          )}
                        </span>
                        {!c.available && c.reason && <span className="text-xs text-on-surface-muted">{c.reason}</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
              {changing && picked?.available && (
                <p className="text-[11px] text-on-surface-muted">
                  Another protocol clears the points picked for this one.{" "}
                  <button
                    type="button"
                    className="font-medium text-brand hover:underline"
                    onClick={() => setChanging(false)}
                  >
                    Keep {picked.label}
                  </button>
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
      {children}
    </div>
  );
}
